import React, { useState, useEffect, useMemo } from 'react';
import { Users, UserCircle, Book, TrendingUp, Calendar, Clock, Activity } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import ApexChart from '../../components/ApexChart';
import api from '../../config/api';
import toast from 'react-hot-toast';

const DEFAULT_MONTHLY = {
  labels: [],
  users: [],
  stories: [],
  revenue: [],
  revenueTarget: 0,
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/dashboard/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const monthlyData = stats?.monthly || DEFAULT_MONTHLY;

  const growthChartOptions = useMemo(
    () => ({
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { columnWidth: '35%', borderRadius: 6 } },
      dataLabels: { enabled: false },
      colors: ['#3b82f6', '#ec4899'],
      grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
      stroke: { show: true, width: 4, colors: ['transparent'] },
      xaxis: { categories: monthlyData.labels },
      yaxis: { labels: { formatter: (value) => Math.round(value) } },
      tooltip: { theme: 'light' },
    }),
    [monthlyData.labels]
  );

  const growthChartSeries = useMemo(
    () => [
      { name: 'New Users', data: monthlyData.users || [] },
      { name: 'Stories Created', data: monthlyData.stories || [] },
    ],
    [monthlyData.users, monthlyData.stories]
  );

  const revenueChartOptions = useMemo(
    () => ({
      chart: { toolbar: { show: false }, stacked: false },
      stroke: { width: [0, 4], curve: 'smooth' },
      dataLabels: { enabled: true, enabledOnSeries: [1], style: { fontSize: '12px' } },
      labels: monthlyData.labels,
      markers: { size: 4, strokeWidth: 2 },
      grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
      yaxis: [
        {
          labels: { formatter: (value) => `$${Number(value || 0).toLocaleString()}` },
        },
      ],
      tooltip: {
        shared: true,
        y: [
          {
            formatter: (val) => `$${Number(val || 0).toLocaleString()}`,
          },
        ],
      },
    }),
    [monthlyData.labels]
  );

  const revenueChartSeries = useMemo(
    () => [
      {
        name: 'Revenue Target',
        type: 'column',
        data: (monthlyData.labels || []).map(() => monthlyData.revenueTarget || 0),
        color: '#a855f7',
      },
      {
        name: 'Actual Revenue',
        type: 'line',
        data: monthlyData.revenue || [],
        color: '#f97316',
      },
    ],
    [monthlyData.labels, monthlyData.revenue, monthlyData.revenueTarget]
  );

  if (loading) {
    return (
      <AdminSidebar>
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
        </div>
      </AdminSidebar>
    );
  }

  const statCards = [
    { title: 'Total Users', value: stats?.totalUsers || 0, icon: Users, color: 'from-blue-500 to-blue-600' },
    { title: 'Total Profiles', value: stats?.totalProfiles || 0, icon: UserCircle, color: 'from-purple-500 to-purple-600' },
    { title: 'Total Stories', value: stats?.totalStories || 0, icon: Book, color: 'from-pink-500 to-pink-600' },
    { title: 'Active Users', value: stats?.activeUsers || 0, icon: Activity, color: 'from-green-500 to-green-600' },
  ];

  const activityCards = [
    { title: 'Stories Today', value: stats?.todayStories || 0, icon: Calendar, color: 'text-yellow-600' },
    { title: 'Stories This Week', value: stats?.weekStories || 0, icon: TrendingUp, color: 'text-blue-600' },
    { title: 'Stories This Month', value: stats?.monthStories || 0, icon: Clock, color: 'text-purple-600' },
  ];

  return (
    <AdminSidebar>
      <div className="space-y-8">
        <div>
          <p className="text-gray-600">Overview of your MyStoryMe platform</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div key={index} className="bg-white rounded-2xl shadow-lg p-6 border border-purple-50">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${card.color}`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <h3 className="text-gray-600 text-sm font-medium mb-1">{card.title}</h3>
                <p className="text-3xl font-bold text-gray-900">{card.value.toLocaleString()}</p>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {activityCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div key={index} className="bg-white rounded-2xl shadow-lg p-6 border border-purple-50">
                <div className="flex items-center justify-between mb-4">
                  <Icon className={`h-8 w-8 ${card.color}`} />
                </div>
                <h3 className="text-gray-600 text-sm font-medium mb-1">{card.title}</h3>
                <p className="text-3xl font-bold text-gray-900">{card.value.toLocaleString()}</p>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Growth Overview</h3>
                <p className="text-gray-500 text-sm">Users vs stories (monthly)</p>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-blue-500" />
                  Users
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-pink-500" />
                  Stories
                </span>
              </div>
            </div>
            <ApexChart type="bar" height={320} options={growthChartOptions} series={growthChartSeries} />
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Revenue Progress</h3>
                <p className="text-gray-500 text-sm">Monthly target vs actual</p>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-purple-500" />
                  Target
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-orange-400" />
                  Actual
                </span>
              </div>
            </div>
            <ApexChart type="line" height={320} options={revenueChartOptions} series={revenueChartSeries} />
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
};

export default AdminDashboard;

